'use client';

import { cn } from '@/lib/utils';
import { motion } from 'motion/react';
import { forwardRef } from 'react';

const MotionDiv: any = motion.div;

export interface LiquidGlassContainerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onAnimationStart' | 'onDragStart' | 'onDragEnd' | 'onDrag' | 'ref'> {
  variant?: 'default' | 'flowing' | 'ripple' | 'wave';
  intensity?: 'subtle' | 'medium' | 'strong';
  children: React.ReactNode;
}

const LiquidGlassContainer = forwardRef<HTMLDivElement, LiquidGlassContainerProps>(
  ({
    className,
    variant = 'default',
    intensity = 'medium',
    children,
    ...props
  }, ref) => {
    const baseStyles = cn(
      // Base container styles
      'relative overflow-hidden rounded-2xl',
      'backdrop-blur-xl',
      'border border-stone-200 dark:border-white/10 dark:border-stone-200 dark:border-white/10 dark:border-white/20',
      'shadow-premium shadow-black/30',
      
      // Glass morphism base
      'bg-gradient-to-br from-white/15 via-white/10 to-white/5',
      
      // Intensity variants
      {
        'bg-white/60 dark:bg-black/20': intensity === 'subtle',
        'bg-gradient-to-br from-white/15 via-white/10 to-white/5': intensity === 'medium',
        'bg-gradient-to-br from-white/25 via-white/15 to-white/10': intensity === 'strong',
      },
      
      className
    );

    const getVariantAnimation = () => {
      switch (variant) {
        case 'flowing':
          return {
            background: [
              'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.1) 100%)',
              'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)',
              'linear-gradient(225deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.1) 100%)',
              'linear-gradient(315deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)',
            ],
          };
        case 'ripple':
          return {
            background: [
              'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
              'radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)',
              'radial-gradient(circle at 40% 60%, rgba(255,255,255,0.1) 0%, transparent 50%)',
              'radial-gradient(circle at 60% 40%, rgba(255,255,255,0.1) 0%, transparent 50%)',
            ],
          };
        case 'wave':
          return {
            background: [
              'linear-gradient(0deg, rgba(255,255,255,0.1) 0%, transparent 25%, transparent 75%, rgba(255,255,255,0.1) 100%)',
              'linear-gradient(90deg, rgba(255,255,255,0.1) 0%, transparent 25%, transparent 75%, rgba(255,255,255,0.1) 100%)',
              'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, transparent 25%, transparent 75%, rgba(255,255,255,0.1) 100%)',
              'linear-gradient(270deg, rgba(255,255,255,0.1) 0%, transparent 25%, transparent 75%, rgba(255,255,255,0.1) 100%)',
            ],
          };
        default:
          return {};
      }
    };

    const animation = getVariantAnimation();
    const shouldAnimate = variant !== 'default' && Object.keys(animation).length > 0;

    return (
      <MotionDiv
        ref={ref}
        className={baseStyles}
        animate={shouldAnimate ? animation : undefined}
        transition={shouldAnimate ? {
          duration: 8,
          repeat: Infinity,
          ease: "linear" as const
        } : undefined}
        {...props}
      >
        {/* Liquid glass overlay layers */}
        <div className="absolute inset-0 rounded-inherit">
          {/* Primary glass layer */}
          <div className="absolute inset-0 rounded-inherit bg-gradient-to-br from-white/20 via-transparent to-white/10 opacity-60" />
          
          {/* Secondary reflection layer */}
          <div className="absolute inset-0 rounded-inherit bg-gradient-to-tl from-white/15 via-transparent to-white/5 opacity-40" />
          
          {/* Top highlight */}
          <div className="absolute top-0 left-0 right-0 h-1/4 rounded-t-inherit bg-gradient-to-b from-white/30 to-transparent opacity-70" />
          
          {/* Side highlights */}
          <div className="absolute top-0 left-0 bottom-0 w-1/4 rounded-l-inherit bg-gradient-to-r from-white/20 to-transparent opacity-50" />
        </div>
        
        {/* Content */}
        <div className="relative z-10 p-6">
          {children}
        </div>
        
        {/* Animated liquid effect */}
        {shouldAnimate && (
          <div className="absolute inset-0 rounded-inherit overflow-hidden">
            <MotionDiv
              className="absolute inset-0 rounded-inherit"
              animate={{
                background: [
                  'conic-gradient(from 0deg at 50% 50%, transparent 0%, rgba(255,255,255,0.05) 25%, transparent 50%, rgba(255,255,255,0.05) 75%, transparent 100%)',
                  'conic-gradient(from 360deg at 50% 50%, transparent 0%, rgba(255,255,255,0.05) 25%, transparent 50%, rgba(255,255,255,0.05) 75%, transparent 100%)',
                ],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "linear" as const
              }}
            />
          </div>
        )}
      </MotionDiv>
    );
  }
);

LiquidGlassContainer.displayName = 'LiquidGlassContainer';

export default LiquidGlassContainer;
